import { useState } from 'react'
import { Plus, Trash2, Search } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Header } from '@/components/layout/Header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { ExchangeBadge } from '@/components/common/ExchangeBadge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getAliases, addAlias, deleteAliasGroup } from '@/lib/api'
import { useExchangeOptions } from '@/hooks/useExchangeOptions'

const MARKET_TYPES = ['spot', 'futures'] as const

export default function Aliases() {
  const queryClient = useQueryClient()
  const exchangeOptions = useExchangeOptions()
  const [search, setSearch] = useState('')
  const [newGroup, setNewGroup] = useState('')
  const [newExchange, setNewExchange] = useState('')
  const [newMarkets, setNewMarkets] = useState<Set<string>>(new Set())
  const [newSymbol, setNewSymbol] = useState('')

  const { data, error: loadError } = useQuery({
    queryKey: ['aliases'],
    queryFn: getAliases,
  })

  const refreshAliasData = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['aliases'] }),
    queryClient.invalidateQueries({ queryKey: ['coin-market-data'] }),
    queryClient.invalidateQueries({ queryKey: ['screener-discovery-snapshot'] }),
  ])

  const addAliasMutation = useMutation({
    mutationFn: async ({
      group,
      exchange,
      markets,
      symbol,
    }: {
      group: string
      exchange: string
      markets: Set<string>
      symbol: string
    }) => {
      const marketList = Array.from(markets)
      if (marketList.length === 0) {
        // No market selected → use bare exchange key
        return addAlias(group, exchange, symbol)
      }
      // Call API for each selected market type
      for (const market of marketList) {
        await addAlias(group, `${exchange}_${market}`, symbol)
      }
    },
    onSuccess: () => {
      setNewGroup('')
      setNewExchange('')
      setNewMarkets(new Set())
      setNewSymbol('')
    },
    // A multi-market save may partly succeed before a later request fails.
    onSettled: refreshAliasData,
  })

  const deleteGroupMutation = useMutation({
    mutationFn: (group: string) => deleteAliasGroup(group),
    onSettled: refreshAliasData,
  })

  const aliases = data?.aliases || {}
  const error = addAliasMutation.error || deleteGroupMutation.error || loadError

  const filteredGroups = Object.entries(aliases).filter(([group]) =>
    group.toLowerCase().includes(search.toLowerCase())
  )

  const toggleMarket = (market: string) => {
    setNewMarkets((prev) => {
      const next = new Set(prev)
      if (next.has(market)) {
        next.delete(market)
      } else {
        next.add(market)
      }
      return next
    })
  }

  const handleAddAlias = () => {
    if (newGroup && newExchange && newSymbol) {
      deleteGroupMutation.reset()
      addAliasMutation.mutate({
        group: newGroup,
        exchange: newExchange,
        markets: newMarkets,
        symbol: newSymbol.toUpperCase(),
      })
    }
  }

  return (
    <div className="flex flex-col">
      <Header title="Aliases" />

      <div className="flex-1 space-y-6 p-6">
        {error && (
          <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {error instanceof Error ? error.message : 'Failed to update aliases'}
          </div>
        )}
        {/* Info Banner */}
        <Card className="border-blue-500/30 bg-blue-500/10">
          <CardContent className="py-4">
            <p className="text-sm text-blue-300">
              <strong>What are Aliases?</strong> Aliases group the same asset across
              different exchanges under a common name. For example, BTC might be listed
              as BTCUSDT on one exchange and BTC-USDT on another.
            </p>
          </CardContent>
        </Card>

        {/* Add Alias Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Add Alias</CardTitle>
            <CardDescription>
              Add a symbol mapping to an alias group
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Group name"
                value={newGroup}
                onChange={(e) => setNewGroup(e.target.value)}
                className="w-40"
              />
              <Select value={newExchange} onValueChange={setNewExchange}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Exchange" />
                </SelectTrigger>
                <SelectContent>
                  {exchangeOptions.map((exchange) => (
                    <SelectItem key={exchange} value={exchange}>
                      <span className="capitalize">{exchange}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-3 rounded-md border px-3 py-2">
                {MARKET_TYPES.map((market) => (
                  <label
                    key={market}
                    className="flex items-center gap-1.5 cursor-pointer select-none"
                  >
                    <Checkbox
                      checked={newMarkets.has(market)}
                      onChange={() => toggleMarket(market)}
                    />
                    <span className="text-sm capitalize">{market}</span>
                  </label>
                ))}
              </div>
              <Input
                placeholder="Symbol"
                value={newSymbol}
                onChange={(e) => setNewSymbol(e.target.value)}
                className="flex-1"
              />
              <Button
                onClick={handleAddAlias}
                disabled={
                  !newGroup ||
                  !newExchange ||
                  !newSymbol ||
                  addAliasMutation.isPending
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search alias groups..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Aliases List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Alias Groups</CardTitle>
            <CardDescription>
              {Object.keys(aliases).length} alias groups configured
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredGroups.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {search ? 'No groups match your search' : 'No aliases configured'}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredGroups.map(([group, exchangeMap]) => (
                  <div
                    key={group}
                    className="rounded-lg border p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-lg">{group}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          addAliasMutation.reset()
                          deleteGroupMutation.mutate(group)
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(exchangeMap).map(([exchange, symbol]) => {
                        const baseExchange = exchange.replace(/_spot$|_futures$/, '')
                        const marketType = exchange.endsWith('_spot')
                          ? 'spot'
                          : exchange.endsWith('_futures')
                            ? 'futures'
                            : ''
                        return (
                          <div
                            key={exchange}
                            className="flex items-center gap-2 rounded-md bg-secondary px-3 py-1.5"
                          >
                            <ExchangeBadge exchange={baseExchange} />
                            <span className="text-sm">{symbol}</span>
                            {marketType && (
                              <Badge variant="outline" className="text-xs">
                                {marketType}
                              </Badge>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
